import React, { useMemo, useState } from 'react';
import { cn } from '../../../utils/cn';
import { ACTION_LABEL, fmtDate, inr, inrCompact, LENSES, proofTier } from '../constants';
import { groupKey, orderedGroupKeys, SortKey, sortInvoices } from '../grouping';
import { InvoiceRecord, Lens } from '../types';
import { LabelChips } from './Chip';

/**
 * Lens-grouped, sortable invoice table — "Altitude 2". Lens chips switch the
 * grouping; column headers toggle the sort; a row click opens the dossier.
 */

interface Props {
	invoices: InvoiceRecord[];
	onOpen: (inv: InvoiceRecord) => void;
}

const COLUMNS: Array<{ key?: SortKey; label: string; right?: boolean }> = [
	{ key: 'num', label: 'Invoice' },
	{ key: 'cust', label: 'Customer' },
	{ key: 'amt', label: 'At stake', right: true },
	{ label: 'Raised', right: true },
	{ label: 'Due', right: true },
	{ label: 'State' },
	{ key: 'tier', label: 'Proof' },
	{ key: 'emails', label: 'Emails', right: true },
	{ key: 'quiet', label: 'Quiet', right: true },
	{ label: 'Action' },
];

export const InvoiceTable: React.FC<Props> = ({ invoices, onOpen }) => {
	const [lens, setLens] = useState<Lens>('state');
	const [sortKey, setSortKey] = useState<SortKey>('amt');
	const [sortDir, setSortDir] = useState<1 | -1>(-1);

	const { groups, keys } = useMemo(() => {
		const g: Record<string, InvoiceRecord[]> = {};
		invoices.forEach(r => {
			const k = groupKey(r, lens);
			(g[k] = g[k] || []).push(r);
		});
		return { groups: g, keys: orderedGroupKeys(g, lens) };
	}, [invoices, lens]);

	const toggleSort = (key?: SortKey) => {
		if (!key) return;
		if (sortKey === key) setSortDir(d => (d * -1) as 1 | -1);
		else {
			setSortKey(key);
			setSortDir(-1);
		}
	};

	return (
		<div className='overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm'>
			{/* Lens chips */}
			<div className='flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3'>
				<span className='text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400'>
					Group by
				</span>
				{LENSES.map(l => (
					<button
						key={l.id}
						onClick={() => setLens(l.id)}
						className={cn(
							'rounded-lg px-3 py-1 text-xs font-semibold transition-colors',
							lens === l.id
								? 'bg-slate-900 text-white shadow-sm'
								: 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
						)}
					>
						{l.label}
					</button>
				))}
				<span className='ml-auto text-xs font-medium text-slate-400'>
					{invoices.length.toLocaleString('en-IN')} records
				</span>
			</div>

			{invoices.length === 0 ? (
				<div className='p-12 text-center text-sm text-slate-400'>no records in this view</div>
			) : (
				<div className='overflow-x-auto'>
					<table className='w-full text-sm'>
						<thead>
							<tr className='border-b border-slate-200 bg-slate-50/80 text-[10.5px] uppercase tracking-[0.06em] text-slate-400'>
								{COLUMNS.map((c, i) => (
									<th
										key={i}
										onClick={() => toggleSort(c.key)}
										className={cn(
											'px-3 py-2.5 font-bold',
											c.right ? 'text-right' : 'text-left',
											c.key && 'cursor-pointer select-none hover:text-slate-700'
										)}
									>
										{c.label}
										{c.key === sortKey ? (
											<span className='text-slate-600'>{sortDir === -1 ? ' ↓' : ' ↑'}</span>
										) : (
											''
										)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{keys.map(gk => {
								const g = sortInvoices(groups[gk], sortKey, sortDir);
								const total = groups[gk].reduce((s, r) => s + (r.amt || 0), 0);
								return (
									<React.Fragment key={gk}>
										<tr className='border-y border-slate-100 bg-slate-50 text-[11.5px] font-bold uppercase tracking-wide text-slate-600'>
											<td colSpan={5} className='px-3 py-2'>
												<span className='text-slate-400'>▸</span> {gk}{' '}
												<span className='ml-1 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500'>
													{g.length}
												</span>
											</td>
											<td colSpan={5} className='px-3 py-2 text-right tabular-nums text-slate-500'>
												{inrCompact(total)}
											</td>
										</tr>
										{g.map(r => (
											<InvoiceRow key={r.num} r={r} onOpen={onOpen} />
										))}
									</React.Fragment>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

const InvoiceRow: React.FC<{ r: InvoiceRecord; onOpen: (inv: InvoiceRecord) => void }> = ({
	r,
	onOpen,
}) => {
	const z = r.z || {};
	const em = r.c?.emails ?? '—';
	const quiet = r.c?.quiet != null ? `${r.c.quiet}d` : '—';
	const act = r.fu ? ACTION_LABEL[r.fu.action] : null;
	const proof = proofTier(r.tier);

	return (
		<tr
			onClick={() => onOpen(r)}
			className='group cursor-pointer border-b border-slate-50 transition-colors hover:bg-sky-50/50'
		>
			<td className='px-3 py-2.5 font-mono text-xs font-medium text-slate-800'>{r.num}</td>
			<td className='px-3 py-2.5 text-slate-600' title={r.cust}>
				<span className='block max-w-[220px] truncate'>{r.cust || '—'}</span>
			</td>
			<td className='px-3 py-2.5 text-right font-bold tabular-nums text-slate-900'>{inr(r.amt)}</td>
			<td className='px-3 py-2.5 text-right tabular-nums text-slate-400'>{fmtDate(z.idate)}</td>
			<td className='px-3 py-2.5 text-right tabular-nums text-slate-600'>
				{fmtDate(z.due)}
				{z.od && z.od > 0 ? (
					<span className='ml-1 rounded bg-red-50 px-1 text-[10px] font-semibold text-red-600'>
						+{z.od}d
					</span>
				) : null}
			</td>
			<td className='px-3 py-2.5'>
				<LabelChips labels={r.labels} auditCorrected={!!r.audit_flag} />
			</td>
			<td className='px-3 py-2.5'>
				<span
					className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', proof.cls)}
					title='Strongest payment evidence on file for this invoice'
				>
					{proof.label}
				</span>
			</td>
			<td className='px-3 py-2.5 text-right tabular-nums text-slate-500'>{em}</td>
			<td className='px-3 py-2.5 text-right tabular-nums text-slate-500'>{quiet}</td>
			<td className='px-3 py-2.5'>
				{act ? (
					<span className={cn('text-xs font-bold', act[1])}>{act[0]}</span>
				) : (
					<span className='text-slate-300'>—</span>
				)}
			</td>
		</tr>
	);
};
